import { ComponentKey } from "@certa/studio-core/src/constants/process.constants";
import {
  RuleType,
  RuleTypeToDBKey
} from "@certa/studio-core/src/constants/rules.constants";

/**
 * The following rule types will render old config UI
 * from @certa/appbuilder instead of JSON Editor
 */
export const studio1ConstraintsUI = [
  RuleType.FieldVisibility,
  RuleType.RAG,
  RuleType.DataInheritance,
  RuleType.DataReduction,
  RuleType.AttachmentInheritance,
  RuleType.AttachmentInheritanceV2,
  RuleType.IntegrationPolling,
  RuleType.IntegrationTrigger,
  RuleType.PDFGeneration,
  RuleType.StepAccess,
  RuleType.StepAction,
  RuleType.StepGroupAccess,
  RuleType.StepVisibility,
  RuleType.WorkflowAccess,
  RuleType.RelatedProcessDataInheritance,
  RuleType.RelatedWorkflowFileConstraintsV2,
  RuleType.Notification,
  RuleType.ReadOnlyFieldConstraints,
  RuleType.VersionNamingConstraints
];

export const ruleTypeData: Record<
  RuleType,
  {
    label: string;
    componentKey: ComponentKey;
    ruleKey: RuleTypeToDBKey;
    description?: string;
  }
> = {
  // Process
  [RuleType.WorkflowAccess]: {
    label: "Process Conditional Access",
    componentKey: ComponentKey.PROCESS,
    ruleKey: RuleTypeToDBKey.WorkflowAccess,
    description:
      "Grant or revoke access to the entire Process when certain conditions are met"
  },

  // Swimlane
  [RuleType.StepGroupAccess]: {
    label: "Swimlane Conditional Access",
    componentKey: ComponentKey.SWIMLANES,
    ruleKey: RuleTypeToDBKey.StepGroupAccess,
    description:
      "Grant or revoke access to this specific Swimlane when certain conditions are met"
  },

  // Step
  [RuleType.StepVisibility]: {
    label: "Step Visibility",
    componentKey: ComponentKey.STEPS,
    ruleKey: RuleTypeToDBKey.StepVisibility,
    description: "Make a Step visible only when certain conditions are met"
  },
  [RuleType.StepAccess]: {
    label: "Step Conditional Access",
    componentKey: ComponentKey.STEPS,
    ruleKey: RuleTypeToDBKey.StepAccess,
    description:
      "Grant or revoke access to this specific Step when certain conditions are met"
  },
  [RuleType.Notification]: {
    label: "Notification",
    componentKey: ComponentKey.STEPS,
    ruleKey: RuleTypeToDBKey.Notification,
    description:
      "A notification email can be sent to designated recipient(s) upon the initiation or completion/submission of this step"
  },
  [RuleType.VersionNamingConstraints]: {
    label: "Step Version Labelling",
    componentKey: ComponentKey.STEPS,
    ruleKey: RuleTypeToDBKey.VersionNamingConstraints,
    description:
      "Customize the version label and color when one or more conditions are met"
  },
  [RuleType.PDFGeneration]: {
    label: "PDF Configuration",
    componentKey: ComponentKey.STEPS,
    ruleKey: RuleTypeToDBKey.PDFGeneration,
    description: "Trigger a PDF generation when one or more conditions are met"
  },
  [RuleType.StepAction]: {
    label: "Auto-Submit / Auto-Revert",
    componentKey: ComponentKey.STEPS,
    ruleKey: RuleTypeToDBKey.StepAction,
    description:
      "Allows for a step to be submitted or reverted back as “doable” when certain conditions are met"
  },

  // Field
  [RuleType.FieldVisibility]: {
    label: "Field Visibility",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.FieldVisibility,
    description:
      "Hide the field, make it mandatory, change its label or validate a regular expression based on certain conditions"
  },
  [RuleType.DataInheritance]: {
    label: "Data Inheritance",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.DataInheritance,
    description:
      "Change the value of the field or inherit an attachment from another field based on certain conditions"
  },
  [RuleType.RAG]: {
    label: "R/A/G Flags",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.RAG,
    description:
      "Highlight a particular field in Red, Amber or Green when certain conditions are met"
  },
  [RuleType.DataReduction]: {
    label: "Math & String Operations",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.DataReduction,
    description:
      "Apply an operator such as Sum, Concatenate, Min/Max and many more on one or more fields"
  },
  [RuleType.ReadOnlyFieldConstraints]: {
    label: "Field Editability",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.ReadOnlyFieldConstraints,
    description:
      "Make a field editable or non-editable based on certain conditions"
  },

  [RuleType.AttachmentInheritance]: {
    label: "Attachment Inheritance V1",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.AttachmentInheritance,
    description:
      "Used to inherit an attachment from another field in a different step"
  },
  [RuleType.AttachmentInheritanceV2]: {
    label: "Attachment Inheritance V2",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.AttachmentInheritanceV2,
    description:
      "Used to inherit an attachment conditionally from another field in a different step"
  },
  [RuleType.RelatedWorkflowFileConstraintsV2]: {
    label: "Cross Workflow File Inheritance",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.RelatedWorkflowFileConstraintsV2,
    description:
      "Set or receive files to/from fields that belong in a different workflow"
  },
  [RuleType.ChildProcessCreation]: {
    label: "Child Workflow Creation",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.ChildProcessCreation,
    description:
      "Create related processes on submission of the step it belongs to, and establish rules for creation"
  },
  [RuleType.IntegrationPolling]: {
    label: "Integration Polling",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.IntegrationPolling,
    description:
      "Triggering an API after a specific interval based on certain conditions, without manual intervention"
  },
  [RuleType.IntegrationTrigger]: {
    label: "Auto-trigger Integrations",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.IntegrationTrigger,
    description: "Trigger an API call only when certain conditions are met"
  },
  [RuleType.RelatedProcessDataInheritance]: {
    label: "Cross-Workflow Inheritance",
    componentKey: ComponentKey.FIELDS,
    ruleKey: RuleTypeToDBKey.RelatedProcessDataInheritance,
    description:
      "Set or receive values to/from fields that belong in a different workflow"
  }
};
